<template>
  <section v-if="hasTable" class="team-table-view mode-card mode-card--accordion">
    <button
      class="accordion-header"
      type="button"
      :aria-expanded="expanded"
      @click="expanded = !expanded"
    >
      <b-icon
        :icon="expanded ? 'chevron-down' : 'chevron-right'"
        size="is-small"
        class="accordion-chevron"
      />
      <span class="section-label">Team</span>
    </button>

    <div v-show="expanded" class="accordion-body">
      <p v-if="!allRows.length" class="team-table-empty">No team members to show for this period.</p>
      <p v-else-if="noActivity" class="team-table-empty">No team activity was recorded for this period.</p>

      <div v-if="rows.length" class="team-table-scroll">
        <table class="mode-table team-table">
          <thead>
            <tr>
              <th
                v-for="column in columns"
                :key="column.key"
                :class="{ 'is-numeric': column.numeric }"
                :aria-sort="ariaSort(column.key)"
              >
                <button type="button" class="team-table-sort" @click="sortBy(column.key)">
                  {{ column.label }}
                  <span v-if="sortKey === column.key" class="team-table-sort__dir">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in sortedRows" :key="row.userId || row.name">
              <td>{{ row.name }}</td>
              <td class="is-numeric">{{ formatCount(row.opened) }}</td>
              <td class="is-numeric">{{ formatCount(row.closed) }}</td>
              <td class="is-numeric">{{ formatHours(row.hours) }}</td>
            </tr>
          </tbody>
          <tfoot v-if="rows.length > 1">
            <tr class="team-table-total">
              <td>Total</td>
              <td class="is-numeric">{{ formatCount(totals.opened) }}</td>
              <td class="is-numeric">{{ formatCount(totals.closed) }}</td>
              <td class="is-numeric">{{ formatHours(totals.hours) }}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <p v-if="uncreditedLine" class="team-table-note">{{ uncreditedLine }}</p>
      <p v-if="otherCreditedLine" class="team-table-note">{{ otherCreditedLine }}</p>
      <p class="team-table-scope">{{ scopeLine }}</p>
    </div>
  </section>
</template>

<script>
import { formatHours, formatCount } from '@/utils/numberFormat.js'

const OTHER_CREDITED_LABELS = [
  ['clientStaff', 'client IT staff'],
  ['tools', 'tool accounts'],
  ['departed', 'departed staff'],
  ['sharedName', 'a name two people on your org chart share'],
  ['notOnOrgChart', 'names that match no one on your org chart'],
]

const SOURCE_LABELS = {
  autotask_api: 'Autotask',
  connectwise_api: 'ConnectWise',
}

// KAN-95: the daily window is a New York calendar day; name the zone the way people say it.
const TIME_ZONE_LABELS = {
  'America/New_York': 'Eastern Time',
}

// Team Table package (KAN-95: daily report only): renders payload.teamTable exactly as the
// backend computed it and filtered it for this viewer. Numbers are never recomputed here; the
// totals row comes from the backend, which sums unrounded hours (Rulebook O1). Default order is by name; columns are sortable. No ranking, color or
// commentary on any row (C7, C8).
export default {
  name: 'TeamTableView',
  props: {
    teamTable: {
      type: Object,
      default: () => null,
    },
    // KAN-103 (LWD v2): leave out people with no opened, closed or hours, and show hours to two
    // decimals. Both off by default, so the v1 report is unchanged.
    hideEmptyRows: {
      type: Boolean,
      default: false,
    },
    hoursDecimals: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      expanded: true,
      sortKey: 'name',
      sortDir: 'asc',
      columns: [
        { key: 'name', label: 'Person', numeric: false },
        { key: 'opened', label: 'Opened', numeric: true },
        { key: 'closed', label: 'Closed', numeric: true },
        { key: 'hours', label: 'Hours', numeric: true },
      ],
    }
  },
  computed: {
    hasTable() {
      return Boolean(this.teamTable && Array.isArray(this.teamTable.rows))
    },
    allRows() {
      return this.hasTable ? this.teamTable.rows : []
    },
    rows() {
      if (!this.hideEmptyRows) return this.allRows
      return this.allRows.filter((r) => r.opened || r.closed || Number(r.hours))
    },
    noActivity() {
      return this.allRows.length > 0 && this.allRows.every((r) => !r.opened && !r.closed && !Number(r.hours))
    },
    sortedRows() {
      const dir = this.sortDir === 'asc' ? 1 : -1
      const key = this.sortKey
      return [...this.rows].sort((a, b) => {
        if (key === 'name') return dir * String(a.name || '').localeCompare(String(b.name || ''), 'en', { sensitivity: 'base' })
        const diff = Number(a[key] || 0) - Number(b[key] || 0)
        return diff !== 0 ? dir * diff : String(a.name || '').localeCompare(String(b.name || ''), 'en', { sensitivity: 'base' })
      })
    },
    totals() {
      const t = this.teamTable?.totals
      if (t) return t
      return this.rows.reduce(
        (acc, r) => ({ opened: acc.opened + Number(r.opened || 0), closed: acc.closed + Number(r.closed || 0), hours: acc.hours + Number(r.hours || 0) }),
        { opened: 0, closed: 0, hours: 0 }
      )
    },
    uncreditedLine() {
      const n = this.teamTable?.uncreditedClosed
      if (n === null || n === undefined || !Number.isFinite(Number(n))) return ''
      const count = Number(n)
      return `${count} ${count === 1 ? 'ticket was' : 'tickets were'} closed with no time logged and no one assigned, such as alerts a tool closed on its own.`
    },
    // Closures credited to someone with no row, with the reason for each part
    // (otherCreditedBreakdown). Same wording as the backend email (otherCreditedClosedLine).
    otherCreditedLine() {
      const n = this.teamTable?.otherCreditedClosed
      if (n === null || n === undefined || !Number.isFinite(Number(n)) || Number(n) <= 0) return ''
      const count = Number(n)
      const subject = `${count} ${count === 1 ? 'ticket was' : 'tickets were'} closed by`
      const breakdown = this.teamTable?.otherCreditedBreakdown || {}
      const parts = OTHER_CREDITED_LABELS
        .map(([key, label]) => [Number(breakdown[key]) || 0, label])
        .filter(([part]) => part > 0)
      if (!parts.length) return `${subject} people or accounts with no row in this table.`
      if (parts.length === 1) return `${subject} ${parts[0][1]}.`
      const listed = parts.map(([part, label]) => `${part} by ${label}`)
      const last = listed.pop()
      return `${subject} people or accounts with no row in this table: ${listed.join(', ')} and ${last}.`
    },
    scopeLine() {
      const w = this.teamTable?.window || {}
      const label = w.label || this.formatWindow(w.start, w.end, w.timeZone)
      const source = SOURCE_LABELS[this.teamTable?.sourceSystem] || 'PSA'
      const tz = TIME_ZONE_LABELS[w.timeZone] || w.timeZone || 'UTC'
      // Rulebook T2: a window that had not ended when the table was computed is labeled partial.
      const partial = w.partial === true ? 'day in progress · ' : ''
      return `${label ? `${label} · ` : ''}${partial}${tz} · ${source} tickets and time entries. Credited to the person who logged time on or was assigned each ticket.`
    },
  },
  methods: {
    formatHours(value) {
      if (this.hoursDecimals !== 1) {
        const num = Number(value)
        const factor = 10 ** this.hoursDecimals
        return ((Number.isFinite(num) ? Math.round(num * factor) / factor : 0) + 0).toFixed(this.hoursDecimals)
      }
      return formatHours(value) ?? '0.0'
    },
    formatCount(value) {
      return formatCount(value) ?? '0'
    },
    sortBy(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortKey = key
        this.sortDir = key === 'name' ? 'asc' : 'desc'
      }
    },
    ariaSort(key) {
      if (this.sortKey !== key) return 'none'
      return this.sortDir === 'asc' ? 'ascending' : 'descending'
    },
    // Window bounds are instants; format them as calendar days in the window's own zone.
    formatWindow(start, end, timeZone = 'UTC') {
      const fmt = (v) => {
        const d = new Date(v)
        if (!v || Number.isNaN(d.getTime())) return ''
        try {
          return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: timeZone || 'UTC' }).format(d)
        } catch {
          return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(d)
        }
      }
      const s = fmt(start)
      const e = fmt(end)
      if (!s) return ''
      return s === e || !e ? s : `${s} – ${e}`
    },
  },
}
</script>

<style lang="scss" scoped>
.mode-card {
  border: 1px solid $color-border;
  border-radius: $radius-md;
  background: $color-surface;
  padding: $space-3;
}

.mode-card--accordion {
  padding: 0;
  overflow: hidden;
}

.accordion-header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  padding: $space-3;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  gap: $space-2;

  &:hover {
    background: $color-surface-alt;
  }
}

.accordion-body {
  padding: 0 $space-3 $space-3;
}

.accordion-chevron {
  flex-shrink: 0;
  color: $color-text-muted;
}

.section-label {
  margin: 0;
  font-size: $font-size-xs;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: $color-text-strong;
}

.team-table-scroll {
  overflow-x: auto;
}

.mode-table {
  width: 100%;
  border-collapse: collapse;
  font-size: $font-size-sm;
}

.mode-table th,
.mode-table td {
  text-align: left;
  padding: $space-2 $space-3;
  border-bottom: 1px solid $color-border;
}

.mode-table th {
  font-size: $font-size-xs;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: $color-text-muted;
}

.mode-table .is-numeric {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.team-table-sort {
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: inherit;
  text-transform: inherit;
  letter-spacing: inherit;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid $color-primary;
    outline-offset: 2px;
  }
}

.team-table-sort__dir {
  margin-left: 0.25rem;
  font-size: 0.7em;
}

.team-table-total td {
  font-weight: 700;
  color: $color-text-strong;
  border-bottom: none;
}

.team-table-empty,
.team-table-note,
.team-table-scope {
  margin: $space-2 0 0;
}

.team-table-scope {
  font-size: $font-size-xs;
  color: $color-text-muted;
}
</style>
